// Image cache utility for comic pages
class ImageCache {
  constructor(maxCacheSize = 50) {
    this.cache = new Map();
    this.maxCacheSize = maxCacheSize;
    this.loadingPromises = new Map();
  }

  // Load image and cache it
  async loadImage(src) {
    // Return cached image if available
    if (this.cache.has(src)) {
      return this.cache.get(src);
    }

    // Return existing promise if already loading
    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src);
    }

    // Create new loading promise
    const loadPromise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        // Cache the loaded image
        this.cache.set(src, img);
        this.loadingPromises.delete(src);
        
        // Clean up cache if it's too large
        this.cleanupCache();
        
        resolve(img);
      };
      
      img.onerror = () => {
        this.loadingPromises.delete(src);
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      img.src = src;
    });

    this.loadingPromises.set(src, loadPromise);
    return loadPromise;
  }

  // Preload images in background
  preloadImages(srcArray) {
    srcArray.forEach(src => {
      if (!this.cache.has(src) && !this.loadingPromises.has(src)) {
        this.loadImage(src).catch(() => {
          // Silently fail for preloading
        });
      }
    });
  }

  // Clean up cache when it gets too large
  cleanupCache() {
    if (this.cache.size > this.maxCacheSize) {
      // Remove oldest entries (FIFO)
      const entries = Array.from(this.cache.entries());
      const entriesToRemove = entries.slice(0, this.cache.size - this.maxCacheSize + 10);
      
      entriesToRemove.forEach(([key]) => {
        this.cache.delete(key);
      });
    }
  }

  // Check if image is cached
  isCached(src) {
    return this.cache.has(src);
  }
}

// Global image cache instance
export const imageCache = new ImageCache(50);
